import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { logError } from '../lib/logger';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import SEO from '../components/SEO';
import { PREFERRED_PARTNERS, PreferredPartnerCard, OtherPartnersRow } from '../components/DistributionPartners';

const DistributionPage = ({ onRequestDemo, onRequestQuote }) => {
  const [activeForm, setActiveForm] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    phone: '',
    message: ''
  });

  const handleSubmit = async (e, formType) => {
    e.preventDefault();
    try {
      const { data } = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/contact/submit`, {
        form_type: formType === 'distribute' ? 'Distribution Inquiry — Distribute CTRL Products' : 'Distribution Inquiry — Represent Your Products',
        name: formData.name,
        email: formData.email,
        company: formData.company,
        company_description: 'Distributor',
        phone: formData.phone,
        message: formData.message,
        product: 'UL101',
      });
      if (!data.success) throw new Error(data.message || 'Submission failed');
    } catch (err) {
      logError('Distribution inquiry failed', err);
      toast.error('We could not submit your inquiry. Please try again or email info@ctrlsys.com.');
      return;
    }
    toast.success("Thank you! We'll be in touch soon.");
    setFormData({ name: '', email: '', company: '', phone: '', message: '' });
    setActiveForm(null);
  };

  return (
    <div className="min-h-screen bg-white">
      <SEO title="Distribution & Partners — ctrl.aero" description="Become an authorized distributor or reseller for CTRL Systems ultrasonic aviation maintenance products." />
      <Header onRequestDemo={onRequestDemo} onRequestQuote={onRequestQuote} />
      
      {/* Hero Section */}
      <section className="relative isolate overflow-hidden pt-28 pb-16 sm:pt-32 sm:pb-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-800">
        <div aria-hidden="true" className="absolute inset-0 opacity-20">
          <img
            src="/images/img_0235.jpeg"
            alt=""
            className="w-full h-full object-cover"
          />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-orange-400 text-xs sm:text-sm font-semibold uppercase tracking-wide mb-3">Where to buy</p>
          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-5 max-w-3xl leading-tight">
            Preferred Distribution Partners
          </h1>
          <p className="text-lg text-blue-100/85 max-w-prose leading-relaxed">
            CTRL Systems ultrasonic troubleshooters, acoustic imagers, and thermal cameras are stocked and supported by two
            preferred partners — Barfield and AvionTEq — backed by a network of regional distributors.
          </p>
        </div>
      </section>

      {/* Preferred partners */}
      <section className="py-16 sm:py-20 bg-white" data-testid="preferred-partners-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid gap-6 lg:grid-cols-2 mb-16">
            {PREFERRED_PARTNERS.map((partner) => (
              <PreferredPartnerCard key={partner.id} partner={partner} />
            ))}
          </div>
          <OtherPartnersRow />
        </div>
      </section>

      {/* Forms Section */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-[#1e2d3b] mb-4">
              Partnership Opportunities
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              Join our distribution network or have CTRL represent your aviation ground support equipment
            </p>
          </div>

          <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
            {/* Distribute CTRL Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-slate-100 hover:border-blue-500 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-[#1e2d3b] mb-4">Distribute CTRL Products</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Interested in distributing CTRL ultrasonic sensors and acoustic imaging cameras in your region? Join our network of authorized distributors.
              </p>
              <Button
                onClick={() => setActiveForm('distribute')}
                className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white"
              >
                Apply to Distribute
              </Button>
            </div>

            {/* CTRL Represent Your Products Card */}
            <div className="bg-white rounded-2xl shadow-xl p-8 border-2 border-slate-100 hover:border-orange-500 transition-all duration-300">
              <div className="w-16 h-16 bg-gradient-to-br from-orange-500 to-orange-600 rounded-xl flex items-center justify-center mb-6">
                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
                </svg>
              </div>
              <h3 className="text-2xl font-bold text-[#1e2d3b] mb-4">Let CTRL Represent Your GSTE</h3>
              <p className="text-slate-600 mb-6 leading-relaxed">
                Have aviation ground support or test equipment? CTRL Systems can help represent and distribute your products to our global network.
              </p>
              <Button
                onClick={() => setActiveForm('represent')}
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white"
              >
                Request Representation
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* Form Modal */}
      {activeForm && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-8">
              <div className="flex justify-between items-start mb-6">
                <h3 className="text-3xl font-bold text-[#1e2d3b]">
                  {activeForm === 'distribute' ? 'Distribute CTRL Products' : 'CTRL Represent Your Products'}
                </h3>
                <button
                  onClick={() => setActiveForm(null)}
                  className="text-slate-400 hover:text-slate-600 transition-colors"
                >
                  <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>

              <form onSubmit={(e) => handleSubmit(e, activeForm)} className="space-y-6">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Name *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Email *
                  </label>
                  <input
                    type="email"
                    required
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Company *
                  </label>
                  <input
                    type="text"
                    required
                    value={formData.company}
                    onChange={(e) => setFormData({ ...formData, company: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Phone
                  </label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">
                    Message *
                  </label>
                  <textarea
                    required
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    rows={5}
                    placeholder={activeForm === 'distribute' ? 'Tell us about your distribution network and market reach...' : 'Describe your products and why CTRL should represent them...'}
                    className="w-full px-4 py-3 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-none"
                  />
                </div>

                <div className="flex gap-4">
                  <Button
                    type="button"
                    onClick={() => setActiveForm(null)}
                    variant="outline"
                    className="flex-1 border-slate-300"
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    className={`flex-1 ${activeForm === 'distribute' ? 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700' : 'bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700'} text-white`}
                  >
                    Submit
                  </Button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <Footer />
    </div>
  );
};

export default DistributionPage;
